import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Draggable, Droppable, DragDropContext } from "@hello-pangea/dnd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import dayjs from "dayjs";
import { Row, Col, Card, Button, Select, Input, Switch, Space, Dropdown, Tag, Badge, Tooltip, Empty } from "antd";
import _, { filter } from "lodash";
import { faClock, faEllipsisH, faEnvelopeOpenText, faScrewdriver, faSortNumericDown, faSortAlphaDown } from "@fortawesome/free-solid-svg-icons";
import ModalViewTask from "./ModalViewTask";
import ModalAddTimeEntry from "./ModalAddTimeEntry";

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

/**
 * Moves an item from one list to another list.
 */
const move = (source, destination, droppableSource, droppableDestination) => {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);
  const [removed] = sourceClone.splice(droppableSource.index, 1);

  destClone.splice(droppableDestination.index, 0, removed);

  const result = {};
  result[droppableSource.droppableId] = sourceClone;
  result[droppableDestination.droppableId] = destClone;

  return result;
};

function TaskBoard(props) {
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [taskBoardUser, setTaskBoardUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [countBeforeRefreshData, setCountBeforeRefreshData] = useState(0);
  const [userPreferences, setUserPreferences] = useState(null);
  const [viewTaskProps, setViewTaskProps] = useState({
    open: false,
    task_id: null,
    refresh: false,
  });
  const [addTimeEntryProps, setAddTimeEntryProps] = useState({
    open: false,
    job_id: null,
    task_id: null,
    client_id: null,
    refresh: false,
  });

  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    // dropped outside the list
    if (!destination) {
      return;
    }
    let task_being_moved = tasks.find((f) => f.id == draggableId);
    let new_task_status = taskStatuses.find((f) => f.id == destination.droppableId);

    let applicable_tasks = tasks.filter((f) => f.task_statuses_xref == destination.droppableId && f.owned_by_id == taskBoardUser && f.id != draggableId).sort((a, b) => a.seq - b.seq);
    applicable_tasks.splice(destination.index, 0, { ...task_being_moved, task_statuses_xref: new_task_status.id });
    applicable_tasks = applicable_tasks.map((m, i) => {
      return { ...m, seq: i };
    });
    let all_tasks = tasks;
    all_tasks = all_tasks.map((m) => {
      let found_task = applicable_tasks.find((f) => f.id == m.id);
      if (found_task) return found_task;
      return m;
    });
    setTasks(all_tasks);
    await props.AxiosRequest("/api/tasks/update/bulk", { tasks: applicable_tasks.filter((f) => f.task_statuses_xref == destination.droppableId && f.owned_by_id == taskBoardUser) }, true);
    setCountBeforeRefreshData(countBeforeRefreshData + 1);
  };

  const BulkUpdateTaskOrder = async (tasks) => {
    let response = await props.AxiosRequest("/api/tasks/update/bulk", { tasks: tasks }, true);
    if (response.data.status === "error") {
      props.MakeNotification(response.data.status, response.data.message);
      return;
    }
    await GetTasks();
    props.MakeNotification("success", "Successfully updated tasks");
  };

  const GetTaskStatuses = async () => {
    let response = await props.AxiosRequest("/api/task-statuses/get/by-user-preference");
    setTaskStatuses(response.data.task_statuses);
  };

  const GetTasks = async () => {
    let response = await props.AxiosRequest("/api/tasks/get/all");
    setTasks(response.data.data);
  };

  const GetUsers = async () => {
    let response = await props.AxiosRequest("/api/user/get/all");
    setUsers(response.data.users);
    setTaskBoardUser(response.data.users.find((user) => user.current_user).id);
  };
  const GetUserPreferences = async () => {
    let response = await props.AxiosRequest("/api/user/preferences/get");
    setUserPreferences(response.data.user_preferences);
  };

  useEffect(() => {
    GetTaskStatuses();
    GetUsers();
    GetUserPreferences();
    GetTasks();
  }, []);
  useEffect(() => {
    if (countBeforeRefreshData > 4) {
      GetTasks();
      setCountBeforeRefreshData(0);
    }
  }, [countBeforeRefreshData]);
  useEffect(() => {
    if (viewTaskProps.refresh || addTimeEntryProps.refresh) {
      setViewTaskProps({ ...viewTaskProps, refresh: false });
      setAddTimeEntryProps({ ...addTimeEntryProps, refresh: false });
      GetTasks();
    }
  }, [viewTaskProps?.refresh, addTimeEntryProps?.refresh]);
  useEffect(() => {
    if (searchTerm.length < 2) return setFilteredTasks(tasks);
    let filtered_list = tasks.filter((f) =>
      _.toLower(f.clients_name).indexOf(_.toLower(searchTerm)) > -1 ||
      _.toLower(f.comments).indexOf(_.toLower(searchTerm)) > -1 ||
      _.toLower(f.description).indexOf(_.toLower(searchTerm)) > -1 ||
      _.toLower(f.job_name).indexOf(_.toLower(searchTerm)) > -1 ||
      _.toLower(f.task_status_name).indexOf(_.toLower(searchTerm)) > -1 ||
      _.toLower(f.item_name).indexOf(_.toLower(searchTerm)) > -1 ||
      _.toLower(f.due_at).indexOf(_.toLower(searchTerm)) > -1 ||
      _.toLower(f.time_by).indexOf(_.toLower(searchTerm)) > -1 ||
      _.toLower(props.MakeDateTimeReadable(f.promised_by)).indexOf(_.toLower(searchTerm)) > -1
        ? true
        : false,
    );
    setFilteredTasks(filtered_list);
  }, [tasks, searchTerm]);
  return (
    <>
      <main className="content">
        <div className="container-fluid">
          <Row>
            <Col span="18">
              <div className="header">
                <h1 className="header-title">Task Board</h1>
              </div>
            </Col>
            <Col span="6">
              <Select
                value={taskBoardUser}
                options={props.MakeUserOptions(users)}
                style={{ minWidth: "100%" }}
                showArrow={true}
                onChange={(selected_id) => {
                  setTaskBoardUser(selected_id);
                }}
              />
              <br />
              <Row className="mt-1" gutter="6">
                <Col span="24">
                  <Input
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                    }}
                  />
                </Col>
                <Col span="6">
                  {/* <Dropdown
                    menu={{
                      items: [
                        {
                          key: "1",
                          label: <Button>Test</Button>,
                        },
                      ],
                    }}
                  >
                    <Button style={{ width: "100%" }} type="primary" icon={<FontAwesomeIcon icon={faScrewdriver} />} />
                  </Dropdown> */}
                </Col>
              </Row>
            </Col>
          </Row>

          <div style={{ display: "flex", overflowX: "scroll" }} className="mt-3">
            <DragDropContext onDragEnd={onDragEnd}>
              <Space align="start">
                {userPreferences?.hide_empty_columns}
                {taskStatuses.map((task_status, ind) => (
                  <Droppable key={ind} droppableId={`${task_status.id}`}>
                    {(provided, snapshot) => (
                      <Card
                        className={
                          userPreferences?.hide_empty_columns === 1 && filteredTasks.filter((f) => f.owned_by_id === taskBoardUser).filter((f) => f.task_statuses_xref === task_status.id).length === 0
                            ? "d-none"
                            : ""
                        }
                        title={<h5 className="card-title col-md-6">{task_status.name}</h5>}
                        extra={
                          <>
                            <Dropdown
                              menu={{
                                items: [
                                  {
                                    key: "1",
                                    label: (
                                      <Link
                                        className="clickable"
                                        onClick={() => {
                                          let relevant_tasks = tasks
                                            .filter((f) => f.task_statuses_xref == task_status.id && f.owned_by_id == taskBoardUser)
                                            .sort((a, b) => a.due_at > b.due_at)
                                            .map((m, i) => {
                                              return { ...m, seq: i };
                                            });
                                          BulkUpdateTaskOrder(relevant_tasks);
                                        }}
                                      >
                                        <FontAwesomeIcon icon={faSortNumericDown} className="mx-2" />
                                        Sort by Due Date
                                      </Link>
                                    ),
                                  },
                                  {
                                    key: "2",
                                    label: (
                                      <Link
                                        className="clickable"
                                        onClick={() => {
                                          let relevant_tasks = tasks
                                            .filter((f) => f.task_statuses_xref == task_status.id && f.owned_by_id == taskBoardUser)
                                            .sort((a, b) => a.clients_name > b.clients_name)
                                            .map((m, i) => {
                                              return { ...m, seq: i };
                                            });
                                          BulkUpdateTaskOrder(relevant_tasks);
                                        }}
                                      >
                                        <FontAwesomeIcon icon={faSortAlphaDown} className="mx-2" />
                                        Sort by Client Name
                                      </Link>
                                    ),
                                  },
                                ],
                              }}
                              placement="bottomLeft"
                              arrow
                            >
                              <Button size="small" type="ghost">
                                <FontAwesomeIcon icon={faEllipsisH} />
                              </Button>
                            </Dropdown>
                          </>
                        }
                        style={{ width: "325px", maxHeight: "75vh" }}
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        {filteredTasks.filter((f) => f.owned_by_id === taskBoardUser).filter((f) => f.task_statuses_xref === task_status.id).length > 0 ? (
                          filteredTasks
                            .filter((f) => f.owned_by_id === taskBoardUser)
                            .filter((f) => f.task_statuses_xref === task_status.id)
                            .sort((a, b) => a.seq - b.seq)
                            .map((task, index) => (
                              <Draggable key={task.id} draggableId={`${task.id}`} index={index}>
                                {(provided, snapshot) => (
                                  <>
                                    <Space ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                      <Badge.Ribbon
                                        className={task_status.end == 1 ? "d-none" : ""}
                                        text={
                                          <Tooltip title={<>Due: {props.MakeDateTimeReadable(task.due_at)}</>}>
                                            {dayjs().isAfter(dayjs(task.due_at), "day") ? "Past Due" : null}
                                            {dayjs().isSame(dayjs(task.due_at), "day") ? "Due Soon" : null}
                                          </Tooltip>
                                        }
                                        color={dayjs().isAfter(dayjs(task.due_at), "day") ? "red" : "orange"}
                                        style={dayjs().isAfter(dayjs(task.due_at), "day") || dayjs().isSame(dayjs(task.due_at), "day") ? {} : { display: "none" }}
                                      >
                                        <Card hoverable className="bg-light mb-2" bodyStyle={{ padding: "1rem" }}>
                                          <Row gutter={[4, 4]}>
                                            <Col span={24} className="text-center">
                                              <b>
                                                <Link to={"/client-details/" + task.clients_tag}>{_.truncate(task.clients_name, { length: 30 })}</Link>
                                              </b>
                                            </Col>
                                            <Col span={24} className="text-center">
                                              <Tooltip
                                                title={
                                                  <>
                                                    {task.jobs_name}&nbsp;/&nbsp;{task.description}
                                                  </>
                                                }
                                              >
                                                <span>{_.truncate(task.jobs_name, { length: 18 })}</span>&nbsp;/&nbsp;<span>{_.truncate(task.description, { length: 18 })}</span>
                                              </Tooltip>
                                            </Col>
                                            <Col span={24} className="text-center">
                                              <Space>
                                                <Tag
                                                  className="btn btn-info btn-sm clickable"
                                                  onClick={() => {
                                                    setAddTimeEntryProps({ ...addTimeEntryProps, open: true, client_id: task.clients_id, job_id: task.jobs_id, task_id: task.id });
                                                  }}
                                                >
                                                  <FontAwesomeIcon className="pe-none mr-1" icon={faClock} /> Add Time
                                                </Tag>
                                                <Tag
                                                  className="btn btn-primary btn-sm clickable"
                                                  onClick={(e) => {
                                                    setViewTaskProps({ ...viewTaskProps, task_id: task.id, open: true, editing: true });
                                                  }}
                                                >
                                                  <FontAwesomeIcon className="pe-none mr-1" icon={faEnvelopeOpenText} /> Edit Task
                                                </Tag>
                                              </Space>
                                            </Col>
                                          </Row>
                                        </Card>
                                      </Badge.Ribbon>
                                    </Space>
                                  </>
                                )}
                              </Draggable>
                            ))
                        ) : (
                          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={"No Tasks"} />
                        )}
                        {provided.placeholder}
                      </Card>
                    )}
                  </Droppable>
                ))}
              </Space>
            </DragDropContext>
          </div>
        </div>
        <ModalViewTask {...props} viewTaskProps={viewTaskProps} setViewTaskProps={setViewTaskProps} />
        <ModalAddTimeEntry {...props} addTimeEntryProps={addTimeEntryProps} setAddTimeEntryProps={setAddTimeEntryProps} />
      </main>
    </>
  );
}

export default TaskBoard;
